import type { IRequestSender } from '../../connection/channel';
import type { SigningCallCtx } from '../../identity/signingSender';
import type { JsonValue } from '../../protocol/jsonValue';
import type { HubRpcInterfaceSchema, MethodSchema } from '../../schema/hubRpcInterfaceSchema';
import { type RootPrincipalSet } from './reflection.interfaces';

/**
 * Portable reflection walk over the `hubrpc.directory` referral tree.
 *
 * The hub's global directory is **referral-only**: it lists one
 * `<prefix>::hubrpc.directory` row per claimed prefix rather than the prefix's
 * leaf interfaces. Flattening that tree into the full interface inventory is the
 * consumer's job — {@link walkHubDetailed} performs the breadth-first walk and
 * is shared by the CLI (`hubrpc ls`, completions, the UI) and the hub's own
 * access-candidate resolution (`fetchFullDirectory`).
 */

/**
 * The sender reflection helpers speak over: the signing decorator that wraps
 * the live channel. Decoupled from the concrete `JsonRpcChannel` so reconnect
 * can swap the underlying channel transparently.
 */
export type ReflectionChannel = IRequestSender<SigningCallCtx>;

export interface ServiceListing {
    readonly serviceId: string;
    readonly interfaceId: string;
    readonly hash: string;
    readonly path?: string;
    /** Optional non-normative description of the owning service. */
    readonly serviceDescription?: string;
    /**
     * Root node ids required to access the owning service, in CNF. Surfaced by
     * the directory; {@link walkHubDetailed} additionally folds transitive
     * requirements from ancestor directories onto descendants.
     */
    readonly rootPrincipalSets?: readonly RootPrincipalSet[];
}

/**
 * A {@link ServiceListing} enriched with the serviceId of the directory that
 * reported it. The CLI uses `discoveredFrom` as the routing target for
 * follow-up reflection (`hubrpc.schemas::get`) — because the directory that
 * listed the interface is the one that actually knows about it, even if the
 * item's own `serviceId` says otherwise (e.g. a hub forwarding an aggregated
 * reference).
 */
export interface DiscoveredListing extends ServiceListing {
    readonly discoveredFrom: string;
}

export interface ListOptions {
    readonly interfaceId?: string;
    readonly serviceId?: string;
    readonly limit?: number;
    readonly timeoutMs?: number;
    /**
     * When set, send the reflection call to that service via form-3
     * (`<target>::hubrpc.directory::list`) instead of the implicit root
     * directory. Used when talking to a hub: the root directory is the hub
     * itself, but per-service reflection lives on each participant.
     */
    readonly target?: string;
}

export async function fetchDirectory(
    channel: ReflectionChannel,
    opts: ListOptions = {},
): Promise<ServiceListing[]> {
    const method = opts.target
        ? `${opts.target}::hubrpc.directory::list`
        : 'hubrpc.directory::list';
    const all: ServiceListing[] = [];
    let cursor: string | undefined;
    while (true) {
        const params: Record<string, JsonValue | undefined> = {};
        if (opts.interfaceId !== undefined) params.interfaceId = opts.interfaceId;
        if (opts.serviceId !== undefined) params.serviceId = opts.serviceId;
        if (opts.limit !== undefined) params.limit = opts.limit;
        if (opts.timeoutMs !== undefined) params.timeoutMs = opts.timeoutMs;
        if (cursor !== undefined) params.cursor = cursor;

        const raw = await channel.sendRequest(method, params);
        const page = raw as unknown as {
            items: {
                serviceId: string;
                interfaceId: string;
                interfaceHash: string;
                path?: string;
                serviceDescription?: string;
                rootPrincipalSets?: readonly RootPrincipalSet[];
            }[];
            nextCursor?: string;
        };
        for (const it of page.items) {
            all.push({
                serviceId: it.serviceId,
                interfaceId: it.interfaceId,
                hash: it.interfaceHash,
                ...(it.path !== undefined ? { path: it.path } : {}),
                ...(it.serviceDescription !== undefined
                    ? { serviceDescription: it.serviceDescription }
                    : {}),
                ...(Array.isArray(it.rootPrincipalSets)
                    ? { rootPrincipalSets: it.rootPrincipalSets }
                    : {}),
            });
        }
        if (!page.nextCursor) break;
        cursor = page.nextCursor;
    }
    return all;
}

export async function fetchSchema(
    channel: ReflectionChannel,
    interfaceId: string,
    hash: string | undefined,
    /**
     * Optional routing target. When set, the schema request is addressed to
     * `<target>::hubrpc.schemas::get` (form-3) so it reaches the service that
     * actually hosts this interface — needed when talking to a hub and the
     * interface lives behind a participant.
     */
    target?: string,
): Promise<HubRpcInterfaceSchema> {
    const method = target
        ? `${target}::hubrpc.schemas::get`
        : 'hubrpc.schemas::get';
    const params: Record<string, JsonValue | undefined> = { interfaceId };
    if (hash !== undefined) params.hash = hash;
    const raw = await channel.sendRequest(method, params);
    const wrap = raw as unknown as { schema: HubRpcInterfaceSchema; };
    return wrap.schema;
}

export function findMethodInSchema(
    schema: HubRpcInterfaceSchema,
    methodName: string,
): MethodSchema | undefined {
    return schema.methods[methodName];
}

/** Default recursion depth when walking the bus. */
export const DEFAULT_WALK_DEPTH = 5;
const DIRECTORY_INTERFACE_ID = 'hubrpc.directory';

/**
 * A sub-directory the walk surfaced but could not enumerate — typically because
 * it is capability-gated (e.g. the `hub` directory, reachable only once the
 * connection holds a capability for it). The services it would have listed are
 * therefore absent from the walk; callers can surface `reason` to explain the
 * gap rather than silently dropping the branch.
 */
export interface InaccessibleDirectory {
    /** The directory target (serviceId) that could not be enumerated. */
    readonly serviceId: string;
    /** The error message from the denied directory lookup. */
    readonly reason: string;
}

export interface WalkHubResult {
    /** Every interface reachable from the directories the walk could read. */
    readonly listings: DiscoveredListing[];
    /**
     * Sub-directories that were referenced but could not be enumerated (e.g.
     * capability-gated). Their services are not present in `listings`.
     */
    readonly inaccessible: InaccessibleDirectory[];
}

/**
 * Recursive bus walk: list a directory, then for each `hubrpc.directory`
 * reference it surfaces on a service we haven't queried yet, recurse. Bounded
 * by `maxDepth`.
 *
 * Every listing is tagged with `discoveredFrom` — the serviceId of the
 * directory that produced it. When the same `(serviceId, interfaceId, hash)`
 * shows up via several directories (e.g. once forwarded by the hub, once
 * directly from the participant), we keep the one whose `discoveredFrom`
 * matches the item's `serviceId`, since that's the directory that
 * authoritatively knows about it.
 *
 * Transitive root-node-id requirements flow down the tree: when the walk
 * recurses into a directory whose own `rootPrincipalSets` include `transitive`
 * reqs, every interface discovered at or below that directory inherits them (as
 * singleton AND-sets, kept transitive so they keep flowing further down). This
 * replaces the hub-side `applyTransitiveReqs` fold that the v1 aggregating
 * directory performed.
 *
 * A sub-directory the walk cannot read is recorded in
 * {@link WalkHubResult.inaccessible} rather than silently dropped. A failure to
 * read the *root* directory is left silent — there is no sub-tree to explain —
 * and simply yields empty results.
 *
 * When {@link WalkHubOptions.unlockGatedDirectory} is supplied, each gated
 * sub-directory is offered to the hook; if it returns `true` (a capability was
 * granted), that directory is re-queued and the walk continues into it — a
 * fixpoint that keeps unlocking newly-revealed gated branches until nothing
 * more can be opened (still bounded by `maxDepth`).
 */
export interface WalkHubOptions {
    readonly maxDepth?: number;
    /**
     * The directory the walk starts from. Defaults to the implicit root
     * directory (form-2 `hubrpc.directory::list`). Pass a serviceId to start at
     * `<rootTarget>::hubrpc.directory::list` — e.g. the hub's own global
     * directory (`'hub'`) when walking from the hub's in-process connection.
     */
    readonly rootTarget?: string;
    /**
     * Invoked once per gated sub-directory the walk encounters. Return `true`
     * if a capability for its `hubrpc.directory::list` was granted and the
     * directory should be re-listed; `false` (or omitted hook) leaves it in
     * {@link WalkHubResult.inaccessible}. Called at most once per `serviceId`.
     */
    unlockGatedDirectory?: (serviceId: string) => Promise<boolean>;
}

export async function walkHubDetailed(
    channel: ReflectionChannel,
    opts: WalkHubOptions = {},
): Promise<WalkHubResult> {
    const maxDepth = opts.maxDepth ?? DEFAULT_WALK_DEPTH;
    const queried = new Set<string>();
    // Map from (serviceId, interfaceId, hash) → current best DiscoveredListing.
    const best = new Map<string, DiscoveredListing>();
    // Map from target serviceId → reason, deduped by target.
    const inaccessible = new Map<string, string>();
    // Gated targets already offered to the unlock hook, so we ask at most once.
    const unlockAttempted = new Set<string>();
    const queue: { target: string | undefined; depth: number; inherited: readonly RootPrincipalSet[]; }[] = [
        { target: opts.rootTarget, depth: 0, inherited: [] },
    ];

    while (queue.length > 0) {
        const { target, depth, inherited } = queue.shift()!;
        const queryKey = target ?? '';
        if (queried.has(queryKey)) continue;
        queried.add(queryKey);

        let listings: ServiceListing[];
        try {
            listings = await fetchDirectory(channel, { target });
        } catch (e) {
            // The root directory failing is unusual and has no parent branch to
            // explain; only a gated sub-directory is worth surfacing.
            if (target !== undefined) {
                // Offer the gated directory to the unlock hook once. If it grants
                // the listing cap, re-queue the directory (clearing the `queried`
                // mark) so the next pass enumerates it — a fixpoint that follows
                // newly-revealed gated branches.
                if (opts.unlockGatedDirectory !== undefined && !unlockAttempted.has(target)) {
                    unlockAttempted.add(target);
                    let unlocked = false;
                    try {
                        unlocked = await opts.unlockGatedDirectory(target);
                    } catch {
                        unlocked = false;
                    }
                    if (unlocked) {
                        queried.delete(queryKey);
                        queue.push({ target, depth, inherited });
                        continue;
                    }
                }
                inaccessible.set(target, (e as Error).message);
            }
            continue;
        }

        for (const item of listings) {
            const effective = inherited.length > 0
                ? [...(item.rootPrincipalSets ?? []), ...inherited]
                : item.rootPrincipalSets;
            const tagged: DiscoveredListing = {
                ...item,
                ...(effective !== undefined && effective.length > 0
                    ? { rootPrincipalSets: effective }
                    : {}),
                discoveredFrom: queryKey,
            };
            const k = `${item.serviceId}\u0000${item.interfaceId}\u0000${item.hash}`;
            const existing = best.get(k);
            if (!existing || _isAuthoritative(tagged, existing)) {
                best.set(k, tagged);
            }
            if (depth < maxDepth
                && item.interfaceId === DIRECTORY_INTERFACE_ID
                && !queried.has(item.serviceId)) {
                const childInherited = [...inherited, ...transitiveReqsOf(item.rootPrincipalSets)];
                queue.push({ target: item.serviceId, depth: depth + 1, inherited: childInherited });
            }
        }
    }
    return {
        listings: [...best.values()],
        inaccessible: [...inaccessible].map(([serviceId, reason]) => ({ serviceId, reason })),
    };
}

/**
 * Backward-compatible wrapper over {@link walkHubDetailed} that returns only the
 * reachable listings, dropping the inaccessible-directory report.
 */
export async function walkHub(
    channel: ReflectionChannel,
    opts: WalkHubOptions = {},
): Promise<DiscoveredListing[]> {
    return (await walkHubDetailed(channel, opts)).listings;
}

/**
 * Extract the `transitive` requirements of a CNF requirement collection as
 * singleton AND-sets (kept transitive), so they keep flowing down further hops.
 */
function transitiveReqsOf(sets: readonly RootPrincipalSet[] | undefined): RootPrincipalSet[] {
    return (sets ?? [])
        .flatMap((set) => set.filter((req) => req.transitive === true))
        .map((req) => [req] as RootPrincipalSet);
}

/**
 * Prefer a listing whose reporter (`discoveredFrom`) equals its own `serviceId`
 * — that's the participant speaking about itself, and is more authoritative
 * than an aggregator forwarding it.
 */
function _isAuthoritative(candidate: DiscoveredListing, current: DiscoveredListing): boolean {
    const candidateDirect = candidate.discoveredFrom === candidate.serviceId;
    const currentDirect = current.discoveredFrom === current.serviceId;
    return candidateDirect && !currentDirect;
}
